import styled from "styled-components";
import { MaterialDesignContent } from "notistack";
import { createTheme } from "@mui/material/styles";

import { BREAKPOINTS, Colors } from "styles";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobileXs: true;
    mobileXl: true;
    tabletXs: true;
    tabletXl: true;
    laptopXs: true;
    laptopXl: true;
  }
}

const FontFamily = Object.freeze({
  admin: ["DM Sans", "sans-serif", "Roboto"].join(","),
  iframe: ["Oswald", "sans-serif", "Roboto"].join(","),
});

export const adminTheme = createTheme({
  palette: {
    mode: "dark",
    warning: { main: "#FF7A00" },
  },
  typography: {
    // fontFamily: ["Roboto"].join(","),
    fontFamily: FontFamily.admin,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
          width: 8,
          height: 8,
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
          minHeight: 24,
          borderRadius: 8,
          backgroundColor: "#FF7A00",
        },
        "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus":
          {
            backgroundColor: "#FF7A00",
          },
        "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active":
          {
            backgroundColor: "#FF7A00",
          },
        "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover":
          {
            backgroundColor: "#FF7A00",
          },
        "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
          backgroundColor: "#2b2b2b",
        },
      },
    },
  },
});

export const iframeTheme = createTheme({
  palette: {
    mode: "dark",
    // background: {
    //   default: "transparent",
    // },
    primary: {
      main: Colors.orange.default,
    },
    warning: {
      main: "#FF7A00",
    },
    secondary: {
      main: "#454545",
    },
    success: {
      main: "rgba(189, 255, 0, 1)",
    },
  },
  breakpoints: {
    values: {
      mobileXs: BREAKPOINTS["mobileXs"].maxWidth + 1,
      mobileXl: BREAKPOINTS["mobileXl"].maxWidth + 1,
      tabletXs: BREAKPOINTS["tabletXs"].maxWidth + 1,
      tabletXl: BREAKPOINTS["tabletXl"].maxWidth + 1,
      laptopXs: BREAKPOINTS["laptopXs"].maxWidth + 1,
      laptopXl: BREAKPOINTS["laptopXl"].maxWidth + 1,
    },
  },
  typography: {
    fontFamily: FontFamily.iframe,
    h1: {
      fontWeight: 600,
      fontSize: "56px",
    },
    h2: {
      fontWeight: 600,
      fontSize: "32px",
    },
    h3: {
      fontWeight: 500,
      fontSize: "24px",
    },
    subtitle1: {
      fontWeight: 300,
      fontSize: "16px",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: "28px",
    },
    button: {
      fontWeight: 500,
      fontSize: "28px",
    },
    body1: {
      fontWeight: 400,
      fontSize: "24px",
    },
    body2: {
      fontWeight: 300,
      fontSize: "24px",
    },
    caption: {
      fontWeight: 400,
      fontSize: "12px",
    },
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTypography: {
      defaultProps: {
        fontFamily: ["Oswald", "sans-serif", "Roboto"].join(","),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: "24px",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        endIcon: ({ theme }) => ({
          [theme.breakpoints.down("mobileXs")]: {
            display: "none",
          },
        }),

        containedPrimary: {
          "&:hover": {
            backgroundColor: `${Colors.orange.hover} !important`,
            boxShadow: "none !important",
          },
          "&:active": {
            boxShadow: "none !important",
            backgroundColor: `${Colors.orange.active} !important`,
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        noOptions: {
          fontWeight: 400,
          fontSize: "16px",
          fontFamily: ["Oswald", "sans-serif", "Roboto"].join(","),
        },
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
          width: 0,
          height: 0,
          display: "none",
          backgroundColor: "transparent",
          "-ms-overflow-style": "none" /* IE and Edge */,
          scrollbarWwidth: "none" /* Firefox */,
          // width: 8,
          // height: 8,
          // backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
          minHeight: 24,
          borderRadius: 8,
          backgroundColor: "#FF7A00",
        },
        "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus":
          {
            backgroundColor: "#FF7A00",
          },
        "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active":
          {
            backgroundColor: "#FF7A00",
          },
        "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover":
          {
            backgroundColor: "#FF7A00",
          },
        "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
          backgroundColor: "#2b2b2b",
        },
      },
    },
  },
});

iframeTheme.typography.h1 = {
  fontWeight: 600,
  fontSize: "56px",
  [iframeTheme.breakpoints.down("tabletXs")]: {
    fontSize: "32px",
  },
};

iframeTheme.typography.h2 = {
  fontWeight: 600,
  [iframeTheme.breakpoints.up("tabletXs")]: {
    fontSize: "32px",
  },
  [iframeTheme.breakpoints.between("mobileXs", "tabletXs")]: {
    fontSize: "24px",
  },
  [iframeTheme.breakpoints.down("mobileXs")]: {
    fontSize: "16px",
  },
};

iframeTheme.typography.h3 = {
  fontWeight: 500,

  [iframeTheme.breakpoints.up("tabletXs")]: {
    fontSize: "24px",
  },
  [iframeTheme.breakpoints.between("mobileXs", "tabletXs")]: {
    fontSize: "20px",
  },
  [iframeTheme.breakpoints.down("mobileXs")]: {
    fontSize: "16px",
  },
};

iframeTheme.typography.body1 = {
  fontWeight: 400,

  [iframeTheme.breakpoints.up("tabletXs")]: {
    fontSize: "24px",
  },
  [iframeTheme.breakpoints.between("mobileXs", "tabletXs")]: {
    fontSize: "20px",
  },
  [iframeTheme.breakpoints.down("mobileXs")]: {
    fontSize: "16px",
  },
};

iframeTheme.components!.MuiFormLabel!.styleOverrides = {
  root: {
    color: "white",
    fontWeight: 500,
    fontFamily: ["Oswald", "sans-serif", "Roboto"].join(","),

    [iframeTheme.breakpoints.up("tabletXs")]: {
      fontSize: "24px",
    },
    [iframeTheme.breakpoints.between("mobileXs", "tabletXs")]: {
      fontSize: "18px",
    },
    [iframeTheme.breakpoints.down("mobileXs")]: {
      fontSize: "16px",
    },
  },
};

iframeTheme.components!.MuiFormControlLabel!.styleOverrides = {
  root: {
    ".MuiFormControlLabel-label": {
      fontWeight: 400,

      [iframeTheme.breakpoints.up("tabletXs")]: {
        fontSize: "24px",
      },
      [iframeTheme.breakpoints.between("mobileXs", "tabletXs")]: {
        fontSize: "18px",
      },
      [iframeTheme.breakpoints.down("mobileXs")]: {
        fontSize: "16px",
      },
    },
  },
};

export const StyledSnackbarComponent = styled(MaterialDesignContent)(() => ({
  "&.notistack-MuiContent-success": {
    fontFamily: FontFamily.iframe,
  },
  "&.notistack-MuiContent-error": {
    fontFamily: FontFamily.iframe,
  },
}));
